/**
 * Curated lexicon of stems whose Pāṇinian analysis cannot be recovered from
 * their spelling or from the dictionary's own markup.
 *
 * These are mostly root-nouns (a verbal root used directly as a nominal stem,
 * √bhū → *bhūḥ, bhuvau, bhuvaḥ*) and compounds ending in one (*su-dhī*). A plain
 * stem of the same shape would decline quite differently — *bhvau* rather than
 * *bhuvau* — because 6.4.77 `aci śnudhātubhruvāṃ yvor iyaṅuvaṅau` fires only for
 * verbal roots.
 *
 * Detecting these automatically does not work: matching a stem's tail against
 * the Dhātupāṭha finds *mātṛ-gaṇa* and *nāga-karṇa* for every genuine *kāṣṭhī-bhū*,
 * roughly one hit in twenty-five. So they are curated instead, and each entry is
 * verified against a reference paradigm in the test suite.
 */

import { StemAnalysis } from "./vidyut-engine";
import { Gender } from "./types";

export type LexiconEntry = {
    analysis: StemAnalysis;
    /** Short gloss shown in the UI, e.g. "su + √dhī (root-noun)". */
    note?: string;
};

/**
 * Keyed by stem in IAST. A stem may decline differently per gender, so entries
 * are keyed by gender with `any` as the fallback.
 */
export type LexiconRecord = Partial<Record<Gender, LexiconEntry>> & { any?: LexiconEntry };

/**
 * Roots are given in their *aupadeśika* form — the shape Pāṇini states them in,
 * including the accent marks and `it` letters that condition later rules. `DI\N`
 * is √dhī of the divādi gaṇa; the trailing `N` marks it as ṅit, which is why it
 * takes ātmanepada endings and matters for the derivation.
 *
 * Every entry here is verified against a reference paradigm in
 * `declension.test.ts`; nothing is added on intuition.
 */
export const EXCEPTION_LEXICON: Record<string, LexiconRecord> = {
    // Root-nouns: the stem *is* a verbal root, so 6.4.77 substitutes iyaṅ/uvaṅ
    // before vowel endings — bhuvau, not bhvau.
    "bhū": {
        fem: {
            analysis: { kind: "krdanta", root: "BU", gana: "Bhvadi", krt: "kvip" },
            note: "√bhū (root-noun)",
        },
    },
    "dhī": {
        fem: {
            analysis: { kind: "krdanta", root: "DI\\N", gana: "Divadi", krt: "kvip" },
            note: "√dhī (root-noun)",
        },
    },

    // A compound whose final member is a root: viśva + √pā "protecting all".
    "viśvapā": {
        mas: {
            analysis: { kind: "krdanta", root: "pA\\", gana: "Bhvadi", krt: "kvip", prefixes: ["viSva"] },
            note: "viśva + √pā (root-noun compound)",
        },
        fem: {
            analysis: { kind: "krdanta", root: "pA\\", gana: "Bhvadi", krt: "kvip", prefixes: ["viSva"] },
            note: "viśva + √pā (root-noun compound)",
        },
    },

    // Agent nouns in -tṛ: the -tṛ is the kṛt affix tṛc, not part of the root, and
    // the strong cases lengthen to -tār- (kartāram) which a plain stem never does.
    "kartṛ": {
        mas: {
            analysis: { kind: "krdanta", root: "qukf\\Y", gana: "Tanadi", krt: "tfc" },
            note: "√kṛ + tṛc (agent noun)",
        },
        neu: {
            analysis: { kind: "krdanta", root: "qukf\\Y", gana: "Tanadi", krt: "tfc" },
            note: "√kṛ + tṛc (agent noun)",
        },
    },

    // -is / -us stems. Their final s becomes ṣ before vowel endings (haviṣā, not
    // havisā) by 8.3.57 `iṇkoḥ`, but 8.3.59 `ādeśapratyayayoḥ` restricts that to
    // an s which is itself an affix or a substitute. Given an opaque stem the
    // engine cannot know that, so the unādi affix has to be stated. The roots
    // below were found by searching the Dhātupāṭha for the derivation that
    // reproduces each stem.
    "havis": {
        neu: {
            analysis: { kind: "krdanta", root: "hu\\", gana: "Juhotyadi", unadi: "isi" },
            note: "√hu + isi",
        },
    },
    "sarpis": {
        neu: {
            analysis: { kind: "krdanta", root: "sf\\px~", gana: "Bhvadi", unadi: "isi" },
            note: "√sṛp + isi",
        },
    },
    "arcis": {
        neu: {
            analysis: { kind: "krdanta", root: "arca~", gana: "Bhvadi", unadi: "isi" },
            note: "√arc + isi",
        },
    },
    "cakṣus": {
        neu: {
            analysis: { kind: "krdanta", root: "ca\\kzi~\\N", gana: "Adadi", unadi: "usi" },
            note: "√cakṣ + usi",
        },
    },
    "dhanus": {
        neu: {
            analysis: { kind: "krdanta", root: "Dana~", gana: "Juhotyadi", unadi: "usi" },
            note: "√dhan + usi",
        },
    },
    "āyus": {
        neu: {
            analysis: { kind: "krdanta", root: "i\\R", gana: "Adadi", unadi: "usi" },
            note: "√i + usi",
        },
    },
    "vapus": {
        neu: {
            analysis: { kind: "krdanta", root: "quva\\pa~^", gana: "Bhvadi", unadi: "usi" },
            note: "√vap + usi",
        },
    },

    // Root-nouns whose final aspirate throws its aspiration back onto the initial
    // consonant when the ending strips it (8.2.37 `eco baśo bhaṣ`): budh gives
    // *bhut*, *bhudbhyām*, *bhutsu*; guh gives *ghuṭ*, *ghuḍbhyām*. The engine
    // applies this only when it knows the stem is a dhātu.
    "budh": {
        mas: {
            analysis: { kind: "krdanta", root: "buDa~", gana: "Bhvadi", krt: "kvip" },
            note: "√budh (root-noun)",
        },
        neu: {
            analysis: { kind: "krdanta", root: "buDa~", gana: "Bhvadi", krt: "kvip" },
            note: "√budh (root-noun)",
        },
    },
    "guh": {
        fem: {
            analysis: { kind: "krdanta", root: "guhU~^", gana: "Bhvadi", krt: "kvip" },
            note: "√guh (root-noun)",
        },
    },
    "pur": {
        fem: {
            analysis: { kind: "krdanta", root: "pura~", gana: "Tudadi", krt: "kvip" },
            note: "√pur (root-noun)",
        },
    },
};
